import Field from '@core/managers/Field';
import Scope from '@core/managers/Scope';
import Base from '@core/managers/Base';

import * as Events from '@core/events';
import {
  TComponent,
  TFormValues,
  TSchema,
  TStepData,
  TComponentPropsMapping,
  TFormEventDirectives,
  TEventsKeys,
  TFields,
} from '@core/types';
import { formFlows, register } from '@core/handlers/flows';
import { EEVents } from '@core/constants';

class Form extends Base {
  #fields: TFields = {};
  #observer: Events.Observer;
  formData: TFormValues;
  formId: string;
  schema: TSchema;
  scopedSchema: TSchema;
  scope: Scope;
  initialValues: Record<string, unknown>;
  #step: TStepData;
  steps: Record<number, TFields> = {};
  group: string;
  constructor(
    formId: string,
    observer: Events.Observer,
    scope: Scope,
    schema: TSchema,
    opts: {
      initialValues: Record<string, unknown>;
    },
    group,
  ) {
    super(observer);
    this.#observer = observer;
    this.schema = schema;
    this.scopedSchema = schema;
    this.scope = scope;
    this.formId = formId;
    this.initialValues = opts?.initialValues;
    this.group = group;
    this.#step = {
      numSteps: schema?.components.length,
      navigated: false,
      index: 0,
      currentStepSchema: schema?.components[0],
      isValid: false,
      data: {},
    };

    this.formData = {
      predictableErroredFields: [],
      erroredFields: [],
      fields: {},
      formatted: {},
      form: {
        scope: scope.getGlobalScope(),
        steps: this.#step,
        isValid: false,
        messages: [],
      },
    };

    register(observer, formFlows, schema);
  }

  get step() {
    return this.#step;
  }

  set step(step: TStepData) {
    this.#step = step;
  }

  get fields() {
    return this.#fields || {};
  }

  eventReducedSchema(event: TEventsKeys): TFormEventDirectives {
    return this.scopedSchema
      ? (Object.keys(this.scopedSchema).reduce((acc, key) => {
          if (!this.scopedSchema[key]) return acc;
          return { ...acc, [key]: this.scopedSchema[key][event] };
        }, {}) as any)
      : ({} as TFormEventDirectives);
  }

  getFieldInstance(
    component: TComponent,
    propsMapping: TComponentPropsMapping = {},
  ) {
    component.name =
      component.name ||
      component.component + '_' + JSON.stringify(component.props);
    if (!this.steps[this.#step.index]) {
      this.steps[this.#step.index] = {};
    }

    if (this.fields[component.name]) {
      return this.fields[component.name];
    }

    this.fields[component.name] = new Field(
      this.#observer,
      component,
      propsMapping,
      this.scope,
    );
    this.steps[this.#step.index][component.name] = this.fields[component.name];
    return this.fields[component.name];
  }

  rehydrate() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { components, ...rest } = this.scopedSchema;
    this.publish(EEVents.ON_FORM_REHYDRATE, {
      checksum: JSON.stringify(rest),
    });
  }

  destroyField(field: string) {
    if (!this.#fields[field]) return;
    delete this.#fields[field];
    delete this.steps[this.#step.index][field];
    this.publish(EEVents.ON_FORM_DESTROY_FIELD, {
      checksum: field,
    });
  }
}

export default Form;
export type { Form };
